// Relative /api — production serves API on the same host; Vite dev proxies /api → :4000.
export const baseURL: string = "/api";

// Interface for HTTP Client Settings
export interface HttpClientSettings {
  method?: string;
  headers?: Record<string, string>;
  body?: FormData | Record<string, unknown> | unknown[];
  auth?: boolean;
  signal?: AbortSignal;
  onlyResponse?: boolean;
  _isRetry?: boolean; // internal — prevents infinite refresh loops
  /**
   * Forwarded straight to fetch(). Lets a request outlive a page unload (browser refresh/
   * close/nav) — unlike navigator.sendBeacon, keepalive fetches can still carry the
   * Authorization header this app's auth relies on. Use for "flush on unload" saves.
   */
  keepalive?: boolean;
}

// Custom Error Class for HTTP Errors
export class HttpError extends Error {
  status: number;
  statusText: string;
  data: unknown;

  constructor(status: number, statusText: string, data: unknown) {
    super((data as any)?.error || (data as any)?.message || statusText);
    this.name = "HttpError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

// ── Silent token refresh state ─────────────────────────────────────────────
// One refresh at a time — concurrent expired requests queue and retry together.
let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

const flushQueue = (token: string | null) => {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
};

const attemptTokenRefresh = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem("refreshToken");
  const expiredToken = localStorage.getItem("token");
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${baseURL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken, accessToken: expiredToken }),
    });
    if (!res.ok) return null;
    const data: any = await res.json();
    if (!data.accessToken) return null;
    localStorage.setItem("token", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
};
// ──────────────────────────────────────────────────────────────────────────

// HTTP Client Function
const httpClient = async (
  input: string,
  settings: HttpClientSettings = {},
): Promise<unknown> => {
  const {
    method = "GET",
    headers,
    body,
    onlyResponse,
    ...remainingSettings
  } = settings;

  // Build the complete URL
  let url: string = input;
  if (!/^https?:\/\//.test(url)) {
    if (url.startsWith("/api/")) {
      // Already prefixed (legacy callers) — do not double-prefix
    } else {
      url = `${baseURL}${url.startsWith("/") ? url : `/${url}`}`;
    }
  }

  // Attach auth token (header + Bearer — authMiddleware accepts both)
  const finalHeaders: Record<string, string> = { ...headers };
  const token = localStorage.getItem("token");
  if (token && token !== "null") {
    finalHeaders["token"] = token;
    finalHeaders["Authorization"] = `Bearer ${token}`;
  }
  if (body && !(body instanceof FormData)) {
    finalHeaders["Content-Type"] = "application/json";
  }

  const fetchOptions: RequestInit = {
    method,
    headers: finalHeaders,
    ...remainingSettings,
  };
  if (body) {
    fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (onlyResponse) {
    return response;
  }

  // Parse response
  const contentType = response.headers.get("content-type");
  let data: unknown;
  try {
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }
  } catch (parseErr) {
    // A 2xx response has already committed on the server (write succeeded) —
    // a body that fails to parse (e.g. truncated/oversized) must never read
    // as a request failure to the caller.
    if (response.ok) {
      data = undefined;
    } else {
      throw parseErr;
    }
  }

  if (!response.ok) {
    switch (response.status) {
      case 304:
        // A 304 carries NO body, so `data` here is "" / undefined — never the
        // cached payload. Returning it as success is what made callers reading
        // `res.meetings || []` blank a list that was already on screen (the
        // Home calendar's "meetings flash and disappear" bug).
        //
        // The API sets `etag: false` so this should not occur; an intermediary
        // (proxy/CDN) can still produce one, and failing loudly beats silently
        // reporting "no results". A caller that wants revalidation semantics
        // must keep its own copy of the last good payload.
        throw new HttpError(304, "Not Modified", {
          error: "Not Modified — response has no body to return",
        });

      case 401: {
        const publicPages = [
          "/login",
          "/signup",
          "/forgot-password",
          "/reset-password",
          "/verify-access",
          "/check-your-email",
          "/verify-email",
          "/accept-invite",
          "/privacy",
        ];

        // Access token expired — try a silent refresh before kicking the user out
        if ((data as any)?.code === "TOKEN_EXPIRED" && !settings._isRetry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              refreshQueue.push(async (newToken) => {
                if (!newToken) {
                  reject(new HttpError(401, "Unauthorized", { error: "Session expired. Please log in again." }));
                  return;
                }
                try {
                  resolve(await httpClient(input, { ...settings, _isRetry: true }));
                } catch (err) {
                  reject(err);
                }
              });
            });
          }

          isRefreshing = true;
          const newToken = await attemptTokenRefresh();
          isRefreshing = false;
          flushQueue(newToken);

          if (newToken) {
            return httpClient(input, { ...settings, _isRetry: true });
          }
          // Refresh failed — fall through to force logout
        }

        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");

        if (!publicPages.includes(window.location.pathname)) {
          window.location.href = "/login";
        }
        throw new HttpError(
          401,
          "Unauthorized",
          data || { error: "Your session has expired. Please log in again." },
        );
      }

      case 403:
        if ((data as any)?.code === "FREE_TIER_LIMIT_REACHED") {
          window.dispatchEvent(new CustomEvent("free-tier-limit-reached", { detail: data }));
        }
        throw new HttpError(403, "Forbidden", data || { error: "You do not have permission to access this resource." });

      case 410: {
        if ((data as any)?.code === "IMPERSONATION_ORG_GONE") {
          const adminToken = localStorage.getItem("adminToken");
          if (adminToken) {
            localStorage.setItem("token", adminToken);
            localStorage.removeItem("adminToken");
            localStorage.removeItem("impersonationContext");
            window.location.href = "/global-admin?tab=manage-orgs";
            return;
          }
        }
        throw new HttpError(410, "Gone", data || { error: "The requested resource is no longer available." });
      }

      case 440: {
        const adminToken = localStorage.getItem("adminToken");
        if (adminToken) {
          localStorage.setItem("token", adminToken);
          localStorage.removeItem("adminToken");
          localStorage.removeItem("impersonationContext");
          window.location.href = "/global-admin?tab=manage-orgs";
          return;
        }
        throw new HttpError(440, "Session Expired", data || { error: "Your impersonation session has expired." });
      }

      case 404:
        throw new HttpError(404, "Not Found", data || { error: "The requested resource was not found." });

      case 402:
        throw new HttpError(402, "Payment Required", data || { error: "Payment required." });

      case 500:
        throw new HttpError(500, "Internal Server Error", data || { error: "An internal server error occurred. Please try again later." });

      default:
        throw new HttpError(response.status, response.statusText, data || { error: `Request failed with status ${response.status}` });
    }
  }

  return data;
};

export default httpClient;
